import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Heading,
    Button,
    Stack,
    Skeleton,
    Text,
    Link,
    Flex,
    useStyleConfig,
    useBreakpointValue,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerBody,
    DrawerCloseButton,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {AdyenCheckout, Card, Dropin} from '@adyen/adyen-web'
import {FormattedMessage, useIntl, defineMessages} from 'react-intl'
import {useHistory} from 'react-router-dom'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {getCreditCardIcon, buildMaskedCardDisplay} from '../../utils/cc-utils'

/* Hooks */
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCustomToast} from '../../contexts/custom-toast-context'
import {VisaIcon, MastercardIcon} from '@salesforce/retail-react-app/app/components/icons'
import {InfoCircleBlack, AmexPaymentIcon} from '../../components/icons'

// MRT multi site
import {getSiteEnvVarValue} from '../../utils/site-config'

/* Components */
/* Styling */
import adyen_css from './styles/adyen.css'

/* Icons */
import {LeftArrow, CustomClose} from '../../components/icons'
import { InfoCard } from '../../components/info-card'

/* Constants */
const messages = defineMessages({
    cardError: {
        id: 'account_detail.payment.card_error',
        defaultMessage: 'There’s an error with the credit card. Please review and try again.'
    },
    errorRemovingCard: {
        id: 'account_detail.payment.error_removing_card',
        defaultMessage: 'There’s an error on removing the credit card.'
    },
    creditCardAdded: {
        id: 'account_detail.payment.credit_card_added',
        defaultMessage: 'Credit card added'
    },
    creditCardRemoved: {
        id: 'account_detail.payment.card_removed',
        defaultMessage: 'Credit card removed'
    },
    isDefault: {
        id: 'account_detail.payment.default',
        defaultMessage: 'Default'
    }
})

/**
 * SavedPaymentMethod is a React functional component that displays a user's saved payment method details,
 * such as card type, last four digits, expiration date, and default status. It provides UI controls for
 * removing the payment method, including a confirmation modal dialog. The component supports responsive
 * design and uses custom hooks for toast notifications and internationalization. It also handles the
 * deletion of a payment instrument via a mutation and refreshes the payment methods list upon successful
 * removal. The component expects properties for payment details, customer identification, and a callback
 * to fetch updated payment methods.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.expirationMonth - The expiration month of the payment method.
 * @param {string} props.expirationYear - The expiration year of the payment method.
 * @param {string} props.id - The unique identifier for the payment method.
 * @param {string} props.type - The type of payment method (e.g., credit card type).
 * @param {string} props.lastFour - The last four digits of the payment method.
 * @param {boolean} props.isDefault - Indicates if this is the default payment method.
 * @param {string} props.customerId - The unique identifier for the customer.
 * @param {function} props.fetchPaymentMethods - Callback to refresh the list of payment methods.
 *
 * @returns {JSX.Element} The rendered payment method card with remove functionality.
 */
const SavedPaymentMethod = ({
    expirationMonth,
    expirationYear,
    id,
    type,
    lastFour,
    isDefault,
    customerId,
    fetchPaymentMethods,
    index
}) => {
    const {showCustomToast} = useCustomToast()
    const intl = useIntl()
    const deleteCustomerPaymentInstrument = useShopperCustomersMutation(
        'deleteCustomerPaymentInstrument'
    )
    const CardTypeToIcon = getCreditCardIcon(type)
    const removeCard = async (id) => {
        try {
            await deleteCustomerPaymentInstrument.mutateAsync({
                parameters: {
                    customerId,
                    paymentInstrumentId: id
                }
            })

            fetchPaymentMethods()
            showCustomToast({
                message: intl.formatMessage(messages.creditCardRemoved)
            })
        } catch {
            showCustomToast({
                message: intl.formatMessage(messages.errorRemovingCard)
            })
        }
    }
    return (
        <InfoCard
            id={id}
            title=""
            isEditable={false}
            isDeletable={true}
            onDelete={() => removeCard(id)}
            vStackSpacing={2}
            hStackAlign="center"
            badge={
                isDefault ? (
                    <Text
                        as="span"
                        fontSize={{base: '11px', lg: '12px'}}
                        textTransform="uppercase"
                        lineHeight="18px"
                        fontWeight="500"
                        border="1px solid black"
                        width="fit-content"
                        padding="0 4px"
                    >
                        <FormattedMessage
                            defaultMessage="Default Credit Card"
                            id="account_detail.default.credit_card"
                        />
                    </Text>
                ) : null
            }
            summary={
                <Box display="flex" alignItems="center" gap="8px">
                    <CardTypeToIcon width="36px" height="36px" />

                    <Text as="span" fontSize={{base: '12px', lg: '14px'}} fontWeight={'400'}>
                        {buildMaskedCardDisplay(lastFour, 4, '•')}
                    </Text>
                </Box>
            }
        />
    )
}

SavedPaymentMethod.propTypes = {
    expirationMonth: PropTypes.string,
    expirationYear: PropTypes.string,
    id: PropTypes.string,
    type: PropTypes.string,
    lastFour: PropTypes.string,
    isDefault: PropTypes.bool,
    customerId: PropTypes.string,
    fetchPaymentMethods: PropTypes.func,
    index: PropTypes.number,
}

/**
 * HeadingPayment is a React functional component that renders a dynamic heading for the payment details section
 * of the user's account page. The heading content and navigation behavior are determined by the `headingMode` prop,
 * which can be one of 'nopayments', 'addcreditcard', or 'savedpayments'. The component adapts its layout and navigation
 * controls for mobile and desktop views using responsive design utilities.
 *
 * On mobile devices, a back button (with a left arrow icon) is conditionally displayed, allowing users to navigate back
 * to the account overview or to trigger a custom callback when adding a new credit card. The heading text is internationalized
 * using FormattedMessage for localization support.
 *
 * Props:
 *   @param {string} headingMode - Determines which heading and navigation controls to display. Supported values are
 *     'nopayments', 'addcreditcard', 'savedpayments', and 'unavailable'.
 *   @param {function} backFromAddCard - Callback function invoked when the back button is pressed in 'addcreditcard' mode.
 *
 * Usage:
 *   <HeadingPayment headingMode="addcreditcard" backFromAddCard={handleBack} />
 *
 * The component leverages Chakra UI for styling and layout, and integrates with React Router for navigation.
 */
const HeadingPayment = ({headingMode, backFromAddCard}) => {
    const isMobile = useBreakpointValue({base: true, lg: false})
    const history = useHistory()

    return (
        <Heading
            display={{base: 'flex'}}
            alignItems={{base: 'center'}}
            justifyContent={{base: 'center', lg: 'start'}}
            as="h2"
            fontSize={{base: '20px', lg: '24px'}}
            lineHeight={{base: '24px', lg: '30px'}}
            fontWeight={{base: 'bold'}}
            tabIndex="0"
            textTransform="uppercase"
            position={'relative'}
        >
            {/* No payment heading */}
            {headingMode === 'nopayments' && headingMode !== 'unavailable' && (
                <React.Fragment>
                    {isMobile && (
                        <Button
                            position={'absolute'}
                            left={'0'}
                            m="0"
                            variant="link"
                            onClick={() => history.push('/account')}
                        >
                            <LeftArrow />
                        </Button>
                    )}
                    <FormattedMessage
                        defaultMessage="Payment Options"
                        id="account_detail.title.saved_payment"
                    />
                </React.Fragment>
            )}

            {/* Add credit card heading */}
            {headingMode === 'addcreditcard' && headingMode !== 'unavailable' && (
                <React.Fragment>
                    {isMobile && (
                        <Button
                            position={'absolute'}
                            left={'0'}
                            m="0"
                            variant="link"
                            onClick={backFromAddCard}
                        >
                            <LeftArrow />
                        </Button>
                    )}
                    <FormattedMessage
                        defaultMessage="Payment Options"
                        id="account_detail.title.saved_payment"
                    />
                </React.Fragment>
            )}

            {/* Saved payments heading */}
            {headingMode === 'savedpayments' && headingMode !== 'unavailable' && (
                <React.Fragment>
                    {isMobile && (
                        <Button
                            position={'absolute'}
                            left={'0'}
                            m="0"
                            variant="link"
                            onClick={() => history.push('/account')}
                        >
                            <LeftArrow />
                        </Button>
                    )}
                    <FormattedMessage
                        defaultMessage="Saved Payment"
                        id="account_detail.title.payment_options"
                    />
                </React.Fragment>
            )}
        </Heading>
    )
}

HeadingPayment.propTypes = {
    headingMode: PropTypes.string,
    backFromAddCard: PropTypes.func
}

/**
 * AddPaymentMethod is a React functional component that provides a user interface for adding a new credit card payment method using Adyen's Drop-in integration.
 *
 * This component handles the entire payment method addition flow, including device fingerprinting, Adyen Drop-in initialization, form validation, error handling, and submission of payment details to the backend. It supports both desktop and mobile layouts, and provides feedback to the user in case of errors or validation issues.
 *
 * Key Features:
 * - Integrates with Adyen's Drop-in UI for secure credit card entry and 3DS authentication.
 * - Dynamically loads Adyen's device fingerprinting script and manages its lifecycle.
 * - Validates cardholder input fields and enables/disables the submission button accordingly.
 * - Handles payment submission, additional details (e.g., 3DS), and error states.
 * - Supports localization and multi-site configurations.
 * - Accepts payment methods data and a callback for when payment is completed.
 *
 * Props:
 * @param {Object} paymentMethods - The available payment methods response from Adyen, used to configure the Drop-in component.
 * @param {Function} onPaymentCompleted - Callback function invoked when the payment method has been successfully added.
 *
 * Usage:
 * <AddPaymentMethod paymentMethods={paymentMethods} onPaymentCompleted={handleCompleted} />
 *
 * Dependencies:
 * - AdyenCheckout, Dropin, Card (Adyen Web SDK)
 * - Chakra UI components and hooks
 * - React Intl for localization
 * - Custom hooks: useBreakpointValue, useStyleConfig, useAccessToken, useMultiSite
 * - PropTypes for prop validation
 */
const AddPaymentMethod = ({paymentMethods, onPaymentCompleted}) => {
    const isMobile = useBreakpointValue({base: true, lg: false})
    const styles = useStyleConfig('CustomAccountAdyenPayments')
    const intl = useIntl()
    const {getTokenWhenReady} = useAccessToken()
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const parameters = {
        ...appConfig.commerceAPI.parameters
    }
    const proxyPath = appConfig.commerceAPI.proxyPath
    const organizationId = parameters.organizationId
    const siteId = parameters.siteId
    const [fingerprintScriptLoaded, setFingerprintScriptLoaded] = useState(false)
    const [isDropinLoaded, setIsDropinLoaded] = useState(false)
    const [dropinConfigsReady, setDropinConfigsReady] = useState(false)
    const {locale} = useMultiSite()
    const paymentContainerRef = useRef(null)
    const dropinRef = useRef(null)
    const [errorMessage, setErrorMessage] = useState(null)
    const [isAddCardDisabled, setIsAddCardDisabled] = useState(true)
    const [notAdditionalDetailsView, setNotAdditionalDetailsView] = useState(true)
    const [fieldValidity, setFieldValidity] = useState({
        holderName: false,
        encryptedCardNumber: false,
        encryptedExpiryMonth: false,
        encryptedExpiryYear: false,
        encryptedSecurityCode: false
    })
    const [setAsDefault, setSetAsDefault] = useState(false)

    const onChange = (data, _component) => {
        const {valid} = data
        const cardType = data?.data?.paymentMethod?.brand

        // Update the security code label based on the card type
        if (_component?.dropinRef?.base?.querySelector('[data-id="encryptedSecurityCode"]')) {
            switch (cardType) {
                case 'amex':
                    _component.dropinRef.base.querySelector('[data-id="encryptedSecurityCode"]').textContent = 'CVV'
                    break;
                default:
                    _component.dropinRef.base.querySelector('[data-id="encryptedSecurityCode"]').textContent = 'CVV'
                    break;
            }
        }

        // Logic to disable the submit button based on field validation
        const relevantFields = {
            holderName: valid.holderName,
            encryptedCardNumber: valid.encryptedCardNumber,
            encryptedExpiryMonth: valid.encryptedExpiryMonth,
            encryptedExpiryYear: valid.encryptedExpiryYear,
            encryptedSecurityCode: valid.encryptedSecurityCode
        }

        setFieldValidity((prevState) => {
            const newState = {
                ...prevState,
                ...relevantFields
            }
            const allFieldsValid = Object.values(newState).every((isValid) => isValid)

            setIsAddCardDisabled(!allFieldsValid)

            return newState
        })
    }

    const ADYEN_CLIENT_KEY = getSiteEnvVarValue(appConfig, 'ADYEN_CLIENT_KEY', siteId)
    const ADYEN_MODE = getSiteEnvVarValue(appConfig, 'ADYEN_MODE', siteId)

    const checkoutConfiguration = {
        clientKey: ADYEN_CLIENT_KEY,
        environment: ADYEN_MODE,
        locale: locale.id,
        countryCode: locale.id.split('-')[1],
        cspNonce: Date.now().toString(),
        amount: {value: 0, currency: locale.preferredCurrency},
        paymentMethodsResponse: paymentMethods?.paymentMethods,
        translations: {
            [locale.id]: {
                /* Labels */
                'creditCard.holderName': 'Cardholder Name',
                'creditCard.cardNumber.label': 'Card Number',
                'creditCard.expiryDate.label': 'Expiry date',
                'creditCard.securityCode.label': 'CVV',

                /* Errors */
                // Holder name errors
                'creditCard.holderName.invalid': 'Please fill out this field',

                // Credit card number errors
                'creditCard.numberField.invalid': 'Please enter the number as it appears on your card',
                'cc.num.900': 'Please enter the number as it appears on your card',
                'cc.num.901': 'Invalid Credit Card Number',

                // Expire date errors
                'cc.dat.912': 'This Credit Card has expired',
                'cc.dat.913': 'This Credit Card has expired',
                'cc.mth.915': 'Please select an Expiration Month',
                'cc.yr.917': 'Enter an expiration date',
                'cc.dat.910': 'Enter a valid expiry date',

                // Security code
                'cc.cvc.920': 'Please enter the security code',
                'cc.cvc.921': 'Invalid Security Code',
            }
        },
        onSubmit: async (state, _component, actions) => {
            try {
                const token = await getTokenWhenReady()
                const body = JSON.stringify({
                    orderId: 'recurringPayment-account',
                    adyenData: state,
                    deviceFingerprint: document.getElementById('adyen-device-fingerprint').value,
                    locale,
                    returnUrl: `${window.location.origin}/account/payment`
                })

                const paymentResult = await fetch(
                    `${proxyPath}/custom/adyen-payments/v1/organizations/${organizationId}/adyen-payments?siteId=${siteId}&c_brand=Garage`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: body
                    }
                )

                const result = await paymentResult.json()
                if (
                    !result.resultCode ||
                    result.resultCode === 'Error' ||
                    (result.additionalData &&
                        result.additionalData['tokenization.store.operationType'] ===
                            'alreadyExisting')
                ) {
                    actions.reject()
                    return
                }

                if (result?.action?.type === 'threeDS2') {
                    setNotAdditionalDetailsView(false)
                }

                actions.resolve(result)
            } catch (error) {
                console.error(
                    '[payment-details - AddPaymentMethod - checkoutConfiguration] onSubmit',
                    error
                )
                actions.reject()
            }
        },
        onAdditionalDetails: async (state, component, actions) => {
            try {
                let result = {}
                const token = await getTokenWhenReady()
                const additionalDetailsResult = await fetch(
                    `${proxyPath}/custom/adyen-payments-details/v1/organizations/${organizationId}/adyen-payments-details?siteId=${siteId}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify(state.data)
                    }
                )

                result = await additionalDetailsResult.json()

                if (!result.adyenData && !result.adyenData.resultCode) {
                    actions.reject()
                    return
                }

                const {resultCode} = result.adyenData

                if (resultCode === 'Refused') {
                    setNotAdditionalDetailsView(true)
                }

                actions.resolve({
                    resultCode
                })
            } catch (error) {
                console.error('onSubmit', error)
                actions.reject()
            }
        },
        onPaymentCompleted: () => {
            onPaymentCompleted()
        },

        onPaymentFailed: async () => {
            setErrorMessage(intl.formatMessage(messages.cardError))
            refreshDropin()
        },

        onError: () => {
            setErrorMessage(intl.formatMessage(messages.cardError))
            refreshDropin()
        }
    }

    let [paymentMethodsConfiguration, setPaymentMethodsConfiguration] = useState({
        card: {
            enableStoreDetails: false,
            showStoredPaymentMethods: false,
            positionHolderNameOnTop: true,
            showPayButton: false,
            showBrandIcon: true,
            hasHolderName: true,
            holderNameRequired: true,
            name: 'Credit Card',
            amount: {value: 0, currency: locale.preferredCurrency},
            styles: {
                base: {
                    fontSize: '14px'
                },
                placeholder: {
                    fontWeight: '400',
                    lineHeight: '24px',
                    color: '#676767',
                    letterSpacing: '0px'
                }
            },
            placeholders: {
                cardNumber: 'Card Number*',
                expiryDate: 'Expiry date*',
                securityCodeThreeDigits: 'CVV*',
                securityCodeFourDigits: 'CVV*',
                holderName: 'Cardholder Name*'
            },
            onLoad: () => {
                setIsDropinLoaded(true)
                setIsAddCardDisabled(!dropinRef?.current?.isValid)
            },
            onChange: onChange
        }
    })

    const refreshDropin = async () => {
        try {
            // Unmount existing dropin
            if (dropinRef.current) {
                dropinRef.current.unmount()
            }

            // Clear the container
            if (paymentContainerRef.current) {
                paymentContainerRef.current.innerHTML = ''
            }

            // Reset states
            setIsDropinLoaded(false)
            setIsAddCardDisabled(true)
            setFingerprintScriptLoaded(false)

            // Recreate dropin
            if (paymentMethods && paymentContainerRef?.current) {
                const checkout = await AdyenCheckout({...checkoutConfiguration, paymentMethods})
                const dropin = new Dropin(checkout, {
                    paymentMethodComponents: [Card],
                    paymentMethodsConfiguration: paymentMethodsConfiguration
                })?.mount(paymentContainerRef?.current)
                dropinRef.current = dropin
            }
        } catch (error) {
            console.error('Error refreshing dropin:', error)
        }
    }

    useEffect(() => {
        if (!isMobile) {
            setPaymentMethodsConfiguration((prevConfig) => {
                const newConfig = {...prevConfig}
                delete newConfig.card.placeholders
                return newConfig
            })
        }

        setDropinConfigsReady(true)
    }, [isMobile])

    useEffect(() => {
        try {
            const createCheckout = async () => {
                if (paymentMethods && paymentContainerRef?.current && dropinConfigsReady) {
                    const checkout = await AdyenCheckout({...checkoutConfiguration, paymentMethods})
                    const dropin = new Dropin(checkout, {
                        paymentMethodComponents: [Card],
                        paymentMethodsConfiguration: paymentMethodsConfiguration
                    })?.mount(paymentContainerRef?.current)
                    dropinRef.current = dropin
                }
            }
            createCheckout()
        } catch (error) {
            console.error('Error creating dropin:', error)
        }
    }, [paymentMethods, dropinConfigsReady])

    useEffect(() => {
        if (!fingerprintScriptLoaded && document.getElementById('adyen-device-fingerprint')) {
            document.getElementById('adyen-device-fingerprint').remove()
        }

        if (!document.getElementById('adyen-device-fingerprint')) {
            const input = document.createElement('input')
            input.type = 'hidden'
            input.id = 'adyen-device-fingerprint'
            input.name = 'adyen-device-fingerprint'
            document.body.appendChild(input)
        }

        if (!fingerprintScriptLoaded) {
            const script = document.createElement('script')
            const now = new Date()
            const yyyy = now.getFullYear()
            const mm = String(now.getMonth() + 1).padStart(2, '0')
            const dd = String(now.getDate()).padStart(2, '0')
            const version = `${yyyy}${mm}${dd}`
            script.src = `https://live.adyen.com/hpp/js/df.js?v=${version}`
            script.async = true
            script.nonce = window.__CSP_NONCE__
            script.onload = () => {
                setFingerprintScriptLoaded(true)
                window.dfSet('adyen-device-fingerprint')
            }
            document.body.appendChild(script)
        }
    }, [fingerprintScriptLoaded])

    const handleSubmitPayment = () => {
        const dropin = dropinRef?.current
        dropin.submit()
    }

    return (
        <Box
            key="adyen-card-container"
            id="card-container"
            css={[adyen_css]}
            sx={styles}
            maxW={{base: 'full', lg: '458px'}}
            margin={{base: '0 auto', lg: '0'}}
            display="flex"
            flexDirection="column"
            height="100%"
        >
            {/* CONTENT AREA */}
            <Box flex="1">
                <Box borderBottom={'1px solid #ECE9E9'} padding={'16px'}>
                    <Text
                        as="h2"
                        fontSize={{base: '16px', lg: '20px'}}
                        lineHeight={'20px'}
                        fontWeight="700"
                        textTransform="uppercase"
                    >
                        <FormattedMessage
                            defaultMessage="Add Credit Card"
                            id="account_detail.title.add_credit_card"
                        />
                    </Text>
                </Box>

                <Box px={'4'} py={'5'}>
                    <Flex align="center" justify="space-between" mb={3}>
                        {/* SUBTITLE */}
                        <Text
                            fontSize={{base: 'xs', lg: 'sm'}}
                            lineHeight={'20px'}
                            fontWeight="700"
                            textTransform="uppercase"
                        >
                            <FormattedMessage
                                defaultMessage="Card Information"
                                id="account_detail.subtitle.card_information"
                            />
                        </Text>

                        {/* CARD ICONS */}
                        <Flex direction="row" gap={'2px'} align="center">
                            <VisaIcon boxSize={8} />
                            <MastercardIcon boxSize={8} />
                            <AmexPaymentIcon boxSize={8} backgroundColor="white" textAlign="end" />
                        </Flex>
                    </Flex>
                    {errorMessage && !isMobile && (
                        <Text
                            paddingBottom={{base: 6}}
                            fontSize={{base: 'xs'}}
                            color={{base: 'dangerText'}}
                        >
                            {errorMessage}
                        </Text>
                    )}

                    <Box
                        className="payment floating-labels account-payment-options"
                        ref={paymentContainerRef}
                        sx={{
                            '.adyen-checkout__input-wrapper': {
                                border: '1px solid #939393 !important',
                                borderRadius: '0 !important'
                            },
                            '.adyen-checkout__label': {
                                left: '10px !important'
                            },
                            '.adyen-checkout__input': {
                                marginLeft: '10px !important',
                                fontSize: '14px !important',
                                fontWeight: '400 !important'
                            },
                            '.adyen-checkout__label__text::after': {
                                content: '" *"',
                                color: '#000',
                                fontWeight: 400,
                                fontSize: '14px'
                            }
                        }}
                    />

                    {errorMessage && isMobile && (
                        <Text fontSize={{base: 'xs'}} color={{base: 'dangerText'}}>
                            {errorMessage}
                        </Text>
                    )}

                    <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
                        {/* CVV NOTE */}
                        <Flex justifyContent={'flex-end'}>
                            <Text
                                fontSize="12px"
                                w={{base: '47%'}}
                                fontWeight={'400'}
                                lineHeight={{base: '16px', lg: '20px'}}
                                color="#585858"
                            >
                                <FormattedMessage
                                    defaultMessage="3–4 digit code found on the back or front of your card."
                                    id="account_detail.text.card_security_code_info"
                                />
                            </Text>
                        </Flex>

                        <Flex flexDirection={'column'} gap={'20px'}>
                            <Box display={'flex'} gap={'8px'} alignItems={'center'}>
                                <input
                                    data-testid="account-checkbox-set-default-credit-card"
                                    type="checkbox"
                                    width={'16px'}
                                    height={'16px'}
                                    checked={setAsDefault}
                                    onChange={(e) => setSetAsDefault(e.target.checked)}
                                />
                                <Text
                                    fontSize="12px"
                                    fontWeight={'400'}
                                    color={'black'}
                                    lineHeight={{base: '16px', lg: '20px'}}
                                >
                                    <FormattedMessage
                                        defaultMessage="Set as my Default Credit Card"
                                        id="account_detail.checkbox.default_credit_card"
                                    />
                                </Text>
                            </Box>

                            <Text
                                fontSize="12px"
                                fontWeight="400"
                                w={'98%'}
                                lineHeight={{base: '16px', lg: '20px'}}
                                color="#676767"
                            >
                                <FormattedMessage
                                    id="account_detail.text.recaptcha_privacy_terms"
                                    defaultMessage="This site is protected by reCAPTCHA and Google {privacyLink} and {termsLink} apply."
                                    values={{
                                        privacyLink: (
                                            <Link
                                                data-testid="account-link-privacy-policy"
                                                href="/privacy-policy.html"
                                                textDecoration="underline"
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Privacy Policy"
                                                    id="account_detail.link.privacy_policy"
                                                />
                                            </Link>
                                        ),
                                        termsLink: (
                                            <Link
                                                data-testid="account-link-terms-of-service"
                                                href="/terms-conditions.html"
                                                textDecoration="underline"
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Terms of Service"
                                                    id="account_detail.link.terms_of_services"
                                                />
                                            </Link>
                                        )
                                    }}
                                />
                            </Text>
                        </Flex>
                    </Box>
                </Box>
            </Box>

            {isDropinLoaded && notAdditionalDetailsView && (
                <Box borderTop={'1px solid #ECE9E9'} px={'4'} pt={'4'}>
                    <Button
                        data-testid="account-button-save-credit-card"
                        onClick={handleSubmitPayment}
                        fontSize={'14px'}
                        fontWeight={'700'}
                        lineHeight={{base: '16px', lg: '20px'}}
                        width={{base: '100%'}}
                        minH={{base: '48px'}}
                        isDisabled={isAddCardDisabled}
                        opacity="1 !important"
                    >
                        <FormattedMessage
                            defaultMessage="Save Credit Card"
                            id="account_detail.title.save_credit_card"
                        />
                    </Button>
                </Box>
            )}
        </Box>
    )
}

AddPaymentMethod.propTypes = {
    paymentMethods: PropTypes.object,
    onPaymentCompleted: PropTypes.func
}

/**
 * PaymentDetails is a React functional component that manages and displays the user's saved payment methods
 * within their account settings. It provides functionality to fetch, display, and add new credit cards,
 * leveraging Adyen's customer wallet API through a proxy. The component adapts its layout responsively
 * for mobile and desktop views, and uses various hooks for internationalization, authentication, and
 * toast notifications. It conditionally renders UI elements based on the presence of saved payment methods,
 * and provides user feedback upon successful addition of a new payment method. The component also handles
 * loading states and ensures the default payment method is prioritized in the display order.
 *
 * @component
 * @returns {JSX.Element} The rendered payment details section, including saved payment methods and the option to add new ones.
 */
const PaymentDetails = () => {
    const intl = useIntl()
    const isMobile = useBreakpointValue({base: true, lg: false})
    const {showCustomToast} = useCustomToast()
    const {data: customer} = useCurrentCustomer()
    const {getTokenWhenReady} = useAccessToken()
    const {app: appConfig} = getConfig()

    const parameters = {
        ...appConfig.commerceAPI.parameters
    }
    const proxyPath = appConfig.commerceAPI.proxyPath
    const organizationId = parameters.organizationId
    const siteId = parameters.siteId
    const [paymentMethods, setPaymentMethods] = useState(null)
    const [showAddPayment, setShowAddPayment] = useState(false)
    const [headingMode, setHeadingMode] = useState('nopayments')
    const [isPMSLoading, setIsPMSLoading] = useState(false)

    const fetchPaymentMethods = async () => {
        setIsPMSLoading(true)

        const token = await getTokenWhenReady()
        const response = await fetch(
            `${proxyPath}/custom/adyen-customer-wallet/v1/organizations/${organizationId}/customer-wallet?siteId=${siteId}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                }
            }
        )
        const responseJson = await response.json()

        if (responseJson && responseJson.cards) {
            // Sort cards to have the default card first
            const sortedCards = responseJson.cards.sort((a, b) => b.default - a.default)
            setPaymentMethods({...responseJson, cards: sortedCards})
        }

        setIsPMSLoading(false)
    }

    useEffect(() => {
        fetchPaymentMethods()
    }, [])

    const handleAddCreditCard = () => {
        setShowAddPayment(true)
        setHeadingMode('addcreditcard')
    }

    const handleBackFromAddCard = () => {
        setShowAddPayment(false)
        setHeadingMode(paymentMethods?.cards?.length === 0 ? 'nopayments' : 'savedpayments')
    }

    const handleOnPaymentCompleted = async () => {
        setShowAddPayment(false)
        setHeadingMode(paymentMethods?.cards?.length === 0 ? 'nopayments' : 'savedpayments')
        showCustomToast({
            message: intl.formatMessage(messages.creditCardAdded)
        })
        await fetchPaymentMethods()
    }

    return (
        <Stack
            data-testid="account-payment"
            spacing={'6'}
            height={{base: '100%'}}
            maxW={{base: '458px'}}
        >
            {isHydrated() && (
                <>
                    <HeadingPayment
                        key="account-payment-heading"
                        headingMode={headingMode}
                        backFromAddCard={handleBackFromAddCard}
                    />
                    <Box
                        data-testid="account-payment"
                        display={{base: 'flex'}}
                        justifyContent={{base: 'space-between'}}
                        flexDirection={{base: 'column'}}
                        flex={{base: '1'}}
                    >
                        <Skeleton
                            isLoaded={!isPMSLoading}
                            display={{base: 'flex'}}
                            justifyContent={{base: 'space-between'}}
                            flexDirection={{base: 'column'}}
                            flex={{base: '1'}}
                        >
                            {/* Add payment */}
                            <>
                                {paymentMethods?.cards?.length === 0 ? (
                                    /* No saved payments */
                                    <Box display={'flex'} flexDirection={'column'} gap={'12px'}>
                                        <Flex align="center" gap="6px">
                                            {/* BLACK DOT */}
                                            <InfoCircleBlack boxSize="16px" />
                                            <Text
                                                fontSize={{base: '12px', lg: '14px'}}
                                                lineHeight={{base: '18px', lg: '24px'}}
                                                fontWeight={{base: '500'}}
                                            >
                                                <FormattedMessage
                                                    defaultMessage="No Saved Payment Options"
                                                    id="account_detail.subtitle.no_saved_payment"
                                                />
                                            </Text>
                                        </Flex>
                                        <Text
                                            fontSize={{base: '12px', lg: '14px'}}
                                            lineHeight={{base: '18px', lg: '24px'}}
                                            fontWeight={{base: '400'}}
                                        >
                                            <FormattedMessage
                                                defaultMessage="Click below to save a payment method."
                                                id="account_detail.subtitle.add_payment_option"
                                            />
                                        </Text>
                                    </Box>
                                ) : (
                                    /* Saved payments */
                                    <Box display={'flex'} flexDirection={'column'} gap={'24px'}>
                                        {paymentMethods?.cards?.map((paymentMethod, index) => (
                                            <SavedPaymentMethod
                                                key={paymentMethod.id}
                                                {...paymentMethod}
                                                fetchPaymentMethods={fetchPaymentMethods}
                                                isDefault={paymentMethod.default}
                                                customerId={customer.customerId}
                                                index={index}
                                            />
                                        ))}
                                    </Box>
                                )}
                                <Button
                                    data-testid="account-button-add-credit-card"
                                    onClick={handleAddCreditCard}
                                    fontSize={'14px'}
                                    display={'flex'}
                                    justifyContent={'center'}
                                    alignItems={'center'}
                                    fontWeight={'700'}
                                    lineHeight={'20px'}
                                    marginTop={{
                                        base: paymentMethods?.cards?.length > 0 ? 6 : 12,
                                        lg: 6
                                    }}
                                    width={{base: 'full', lg: '225px'}}
                                    h={{base: '48px'}}
                                >
                                    <FormattedMessage
                                        defaultMessage="Add Credit Card"
                                        id="account_detail.title.add_credit_card"
                                    />
                                </Button>
                                <Drawer
                                    data-testid="account-drawer-add-payment"
                                    isOpen={showAddPayment}
                                    placement={isMobile ? 'bottom' : 'right'}
                                    onClose={() => setShowAddPayment(false)}
                                    size={isMobile ? 'full' : 'md'}
                                >
                                    <DrawerOverlay bg="rgba(0, 0, 0, 0.30)" />

                                    <DrawerContent
                                        marginTop={{
                                            base: 'auto',
                                            lg: '0'
                                        }}
                                        maxH={{
                                            base: '67vh',
                                            lg: '100vh'
                                        }}
                                        maxW={{
                                            base: '100%',
                                            lg: '458px'
                                        }}
                                        overflow="hidden"
                                        background="white"
                                    >
                                        <DrawerCloseButton
                                            data-testid="account-button-close-add-payment"
                                            mt={'18px'}
                                        />
                                        <DrawerBody
                                            sx={{
                                                paddingTop: '16px !important',
                                                paddingRight: '0 !important',
                                                paddingBottom: '16px !important',
                                                paddingLeft: '0 !important'
                                            }}
                                        >
                                            <AddPaymentMethod
                                                paymentMethods={paymentMethods}
                                                onPaymentCompleted={() => {
                                                    setShowAddPayment(false)
                                                    handleOnPaymentCompleted()
                                                }}
                                            />
                                        </DrawerBody>
                                    </DrawerContent>
                                </Drawer>
                            </>
                        </Skeleton>
                    </Box>
                </>
            )}
        </Stack>
    )
}

PaymentDetails.getTemplateName = () => 'account-payment'

export default PaymentDetails
